import React from 'react';
import { Link } from 'react-router-dom';
import { eventRelativeLabel, formatEventWhen, isEventUpcoming } from '../../utils/eventTime';
import { isSafeHttpUrl, linkLabel } from '../../utils/links';
import { coverOf } from '../../utils/eventImages';
import AddToCalendarButton from './AddToCalendarButton';
import EventKindBadge from './EventKindBadge';
import { AUDIENCE_BADGES, eventAudiences, lowestAudience } from '../../utils/membership';

/**
 * Eine Termin-Karte. Wird öffentlich, im Mitgliederbereich und in der
 * Admin-Liste identisch verwendet - deshalb liegt sie in shared/ und nicht
 * in einem der drei Bereiche.
 *
 * @param {string} [to]          Ziel der Detailseite. Ist es gesetzt, wird die
 *                               GANZE Karte anklickbar (siehe unten) und unten
 *                               steht zusätzlich ein Knopf.
 * @param {string} [detailLabel] Beschriftung dieses Knopfes. Im
 *                               Mitgliederbereich führt er zu Zusagen und
 *                               Rückfragen, öffentlich nur zum Termin - das
 *                               soll draufstehen.
 */
export default function EventCard({ event, to, detailLabel = 'Termin anzeigen', children }) {
  const past = !isEventUpcoming(event);
  const relative = eventRelativeLabel(event);
  // Die offenste Stufe ist die Aussage, die auf die Karte gehoert: "Nur Crew"
  // sagt etwas, "Crew, Mitglieder und Gaeste" liest niemand.
  const audience = lowestAudience(eventAudiences(event));
  const cover = coverOf(event);

  // `mt-event--card` unterscheidet die Karte von den beiden Detailseiten, die
  // dieselben `mt-event__*`-Klassen benutzen. Nur auf der Karte wird die
  // Beschreibung gekuerzt - auf der Detailseite gehoert sie vollstaendig hin,
  // das ist ja der Grund, dorthin zu klicken.
  return (
    <article className={`mt-card mt-event mt-event--card ${past ? 'mt-event--past' : ''}`}>
      {cover && (
        <div className="mt-event__cover">
          <img src={cover} alt={`Titelbild: ${event.title}`} loading="lazy" />
        </div>
      )}

      <div className="mt-event__head">
        <time className="mt-event__date" dateTime={event.startAt?.toDate?.().toISOString?.()}>
          {formatEventWhen(event)}
        </time>
        {relative && <span className="mt-event__relative">{relative}</span>}
        {/* Die Art steht auf JEDER Karte - Ausfahrt, Event oder Stammtisch
            ist die erste Frage, die ein Termin beantworten muss. Die
            Zielgruppe dagegen nur, wenn sie eingeschraenkt ist. */}
        <EventKindBadge event={event} />
        {audience && audience !== 'public' && (
          <span className={`mt-badge mt-badge--audience-${audience}`}>
            {AUDIENCE_BADGES[audience]}
          </span>
        )}
      </div>

      {/* Die ganze Karte ist anklickbar, aber der Link sitzt AM TITEL und
          spannt sich per ::after darüber (siehe .mt-event__titlelink in
          App.css). Ein <a> um die ganze Karte wäre ungültiges HTML - darin
          liegen Knöpfe und weitere Links - und für Screenreader wäre der
          Linktext der komplette Kartentext. So bleibt genau ein Link mit dem
          Titel als Beschriftung, und die Knöpfe darin funktionieren weiter. */}
      <h3 className="mt-event__title">
        {to ? (
          <Link to={to} className="mt-event__titlelink">
            {event.title}
          </Link>
        ) : (
          event.title
        )}
      </h3>

      {event.organizedBy && (
        <p className="mt-event__host">Organisiert von {event.organizedBy}</p>
      )}

      {event.location && (
        <p className="mt-event__location">
          <span aria-hidden="true">📍</span> {event.location}
        </p>
      )}

      {event.description && <p className="mt-event__text">{event.description}</p>}

      {/* isSafeHttpUrl statt eines blossen Vorhandensein-Checks: Der Link kommt
          aus einer Eingabemaske, und ein "javascript:"-href waere ausfuehrbarer
          Code. Altbestand aus der Firebase-Console faellt hier ebenfalls raus. */}
      {isSafeHttpUrl(event.link) && (
        <p className="mt-event__link">
          <a href={event.link} target="_blank" rel="noopener noreferrer">
            <span aria-hidden="true">🔗</span> {linkLabel(event.link)}
          </a>
        </p>
      )}

      {/* Bei einem vergangenen Termin waere der Knopf sinnlos. */}
      {!past && (
        <div className="mt-event__cal">
          <AddToCalendarButton event={event} />
        </div>
      )}

      {children && <div className="mt-event__actions mt-row">{children}</div>}

      {/* Bewusst NACH den Aktionen: Auf der Dashboard-Karte will man erst
          zusagen und dann weiterlesen, nicht umgekehrt. */}
      {to && (
        <div className="mt-event__detail">
          <Link to={to} className="mt-btn mt-btn--secondary mt-btn--sm">
            {detailLabel}
          </Link>
        </div>
      )}
    </article>
  );
}
